"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useSession, signIn } from "next-auth/react";
import { usePrivy } from "@privy-io/react-auth";
import { clientLogger } from "@/lib/client-logger";

/**
 * When Privy authenticates a user, exchange their access token for a NextAuth
 * session so the rest of the app (Navigation's useSession, API session checks,
 * etc.) treats them as logged in. Mounted inside PrivyProvider so usePrivy()
 * is safe, and inside SessionProvider so signIn() triggers a session refresh.
 *
 * Renders nothing — pure effect.
 */
export function PrivyNextAuthBridge() {
  const { authenticated, getAccessToken, logout, ready } = usePrivy();
  const { status, update } = useSession();
  const router = useRouter();
  // Prevent duplicate sign-in attempts inside one Privy-auth'd session.
  // Cleared when Privy logs out so a later sign-in can retry.
  const attempted = useRef(false);
  // Set when NextAuth signs out while Privy is still authenticated. Blocks the
  // re-signin effect from immediately pulling the user back in (a race with the
  // Privy logout below). Cleared once Privy actually logs out.
  const signedOut = useRef(false);
  // Track the last observed NextAuth status so we can detect the
  // authenticated -> unauthenticated transition (user signed out) and mirror
  // it into Privy. Without this, the effect above would immediately re-sign
  // them back in because Privy would still be authenticated.
  const prevStatus = useRef(status);

  useEffect(() => {
    if (prevStatus.current === "authenticated" && status === "unauthenticated" && authenticated) {
      signedOut.current = true;
      clientLogger.info("NextAuth sign-out detected — logging out of Privy to match");
      logout().catch((err) => {
        clientLogger.warn("Failed to log out of Privy after NextAuth sign-out", {
          error: err instanceof Error ? err.message : "Unknown",
        });
      });
    }
    prevStatus.current = status;
  }, [status, authenticated, logout]);

  useEffect(() => {
    if (!ready) return;

    if (!authenticated) {
      attempted.current = false;
      // Privy is gone — a deliberate sign-out is complete, so a future fresh
      // login is allowed to establish a session again.
      signedOut.current = false;
      return;
    }

    if (status !== "unauthenticated") return;
    // The user just signed out of NextAuth; don't re-sign them in while Privy
    // is still tearing down. Once Privy logs out, the branch above resets this.
    if (signedOut.current) return;
    if (attempted.current) return;

    attempted.current = true;
    (async () => {
      try {
        const token = await getAccessToken();
        if (!token) {
          clientLogger.warn("Privy reported authenticated but no access token available");
          attempted.current = false;
          return;
        }
        const result = await signIn("privy", { token, redirect: false });
        if (result?.error) {
          clientLogger.warn("Privy->NextAuth bridge sign-in failed", { error: result.error });
          attempted.current = false;
        } else {
          clientLogger.info("Privy->NextAuth bridge sign-in successful");
          // Bind any anonymous IYK taps from this browser to the now-authenticated
          // user. Stashed for the dashboard banner.
          try {
            const attachRes = await fetch("/api/auth/attach-taps", { method: "POST" });
            if (attachRes.ok) {
              const attached = await attachRes.json();
              if (attached.attachedCount > 0 && typeof window !== "undefined") {
                window.sessionStorage.setItem(
                  "sh_attached_taps",
                  JSON.stringify({
                    attachedCount: attached.attachedCount,
                    campaigns: attached.campaigns,
                  })
                );
              }
            }
          } catch (err) {
            clientLogger.warn("attach-taps call failed after Privy bridge", {
              error: err instanceof Error ? err.message : "Unknown",
            });
          }
          // Getting SessionProvider to re-pick the cookie after signIn({
          // redirect: false }) is famously unreliable in NextAuth v4. Three
          // nudges, each a known workaround, stacked because any one alone
          // has failed in the wild:
          //   1. Dispatch a synthetic 'storage' event — SessionProvider
          //      listens for this to trigger an internal refetch.
          //   2. update() — explicit refetch API.
          //   3. router.refresh() — re-runs server components that read the
          //      session via getServerSession.
          if (typeof window !== "undefined") {
            window.dispatchEvent(new StorageEvent("storage"));
          }
          await update();
          router.refresh();
        }
      } catch (err) {
        clientLogger.warn("Privy->NextAuth bridge threw", {
          error: err instanceof Error ? err.message : "Unknown",
        });
        attempted.current = false;
      }
    })();
  }, [ready, authenticated, status, getAccessToken, update, router]);

  return null;
}
